import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { theme } from "../../Assets/Theme/theme";
import SelectableButton from "./SelectableButton";
import CustomCalendar from "../Common/CustomCalendar";
import axios from "axios";
import { BASE_URL } from "../../Assets/Constants";

function FilterModal({ open, setOpen, data, onApplyFilters, onClearFilters }) {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const filteredData = React.useMemo(() => {
    const seen = new Set();
    return data.filter(item => {
      if (seen.has(item._id)) {
        return false;
      }
      seen.add(item._id);
      return true;
    });
  }, [data]);

  const uniqueTypes = React.useMemo(() => [...new Set(filteredData.map((item) => item.type))], [filteredData]);

  const uniqueCountries = React.useMemo(() => {
    return Array.from(
      new Set(
        filteredData.map((item) => {
          const parts = item?.country_city?.split(", ");
          return parts[parts.length - 1];
        })
      )
    );
  }, [filteredData]);

  // const uniqueCountries = ["United States", "Pakistan", "UK", "Australia", "Germany"]; 

  const onClose = () => setOpen(false);

  const toggleFilter = (filter) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((item) => item !== filter)
        : [...prev, filter]
    );
  };

  const formatDateDDMMYY = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = async () => {
    let formattedDate = null;

    if (selectedDate) {
      try {
        const [day, month, year] = selectedDate?.split("/");
        formattedDate = new Date(`${year}-${month}-${day}`).toISOString()?.split("T")[0];
      } catch (error) {
        console.error("Invalid selectedDate format:", selectedDate, error);
        formattedDate = null;
      }
    }
    const filters = {
      category: selectedFilters.filter((filter) =>
        uniqueTypes.includes(filter)
      ),
      country: selectedFilters.filter((filter) =>
        uniqueCountries.includes(filter)
      ),
      popularity: ["Trending Now", "Top Rated Experiences"],
      date: formattedDate,
      currencysymbol: "$",
    };

    try {
      const response = await axios.post(`${BASE_URL}/user/filter/experience`, filters, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      onApplyFilters(response.data?.data || []);
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedFilters([]);
      setSelectedDate(null);
      setShowCalendar(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "400px", md: "500px" },
          bgcolor: "#fff",
          borderRadius: "12px",
          boxShadow: 24,
          p: 4,
          position: "relative",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: theme.colors.text,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight="700" sx={{ mb: 2, fontSize: "24px" }}>
          Filter
        </Typography>

        {/* Category Section */}
        <Typography sx={{ mb: 1, fontWeight: 700, fontSize: "20px" }}>
          Category
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
          {uniqueTypes && uniqueTypes.length > 0 ? (
            uniqueTypes.map((type) => (
              <SelectableButton
                key={type}
                label={type}
                selected={selectedFilters.includes(type)}
                onClick={() => toggleFilter(type)}
              />
            ))
          ) : (
            <Typography variant="body1" color="textSecondary">
              No type available
            </Typography>
          )}
        </Box>


        <Typography sx={{ mb: 1, fontWeight: 700, fontSize: "20px" }}>
          Popularity
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 3,
          }}
        >
          <SelectableButton
            label="Trending Now"
            selected={selectedFilters.includes("Trending Now")}
            onClick={() => toggleFilter("Trending Now")}
          />
          <SelectableButton
            label="Most Booked"
            selected={selectedFilters.includes("Most Booked")}
            onClick={() => toggleFilter("Most Booked")}
          />
          <SelectableButton
            label="Top-Rated Experiences"
            selected={selectedFilters.includes("Top-Rated Experiences")}
            onClick={() => toggleFilter("Top-Rated Experiences")}
          />
        </Box>

        {/* Country Section */}
        <Typography sx={{ mb: 1, fontWeight: 700, fontSize: "20px" }}>
          Country
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {uniqueCountries && uniqueCountries.length > 0 ? (
            uniqueCountries.map((country) => (
              <SelectableButton
                key={country}
                label={country}
                selected={selectedFilters.includes(country)}
                onClick={() => toggleFilter(country)}
              />
            ))
          ) : (
            <Typography variant="body1" color="textSecondary">
              No country available
            </Typography>
          )}
        </Box>


        {/* Date Section */}
        <Typography sx={{ mb: 1, fontWeight: 700, fontSize: "20px" }}>
          Date
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            border: "1px solid #FF7900",
            padding: "10px",
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: "16px",
              color: theme.colors.text,
              fontWeight: 400,
            }}
          >
            {selectedDate ? `${selectedDate}` : "YYYY/MM/DD"}
          </Typography>
          <IconButton
            onClick={() => setShowCalendar((prev) => !prev)}
            sx={{
              color: theme.colors.orange,
            }}
          >
            <CalendarTodayIcon />
          </IconButton>
        </Box>

        {showCalendar && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 3,
            }}
          >
            <CustomCalendar
              onDateChange={(date) => {
                setSelectedDate(formatDateDDMMYY(date));
                setShowCalendar(false);
              }}
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              flex: 1,
              color: theme.colors.orange,
              borderColor: theme.colors.orange,
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "18px",
              "&:hover": {
                backgroundColor: "rgba(255, 121, 0, 0.1)",
              },
            }}
            onClick={() => {
              setSelectedFilters([]);
              setSelectedDate(null);
              onClearFilters();
              setOpen(false)
            }}

          >
            Clear Filter
          </Button>
          <Button
            variant="contained"
            sx={{
              flex: 1,
              backgroundColor: theme.colors.orange,
              color: "#fff",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "18px",
              "&:hover": {
                backgroundColor: theme.colors.orange,
              },
            }}
            onClick={applyFilters}
          >
            Filter
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default FilterModal;